/**
 * @file DeliveredNotBilledPanel.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-03
 * @modified 2026-10-07
 * @description "Delivered, not billed" on Search Bills: purchase orders of
 *              every kind (PO-D, PO-H, Xero-made PO-) whose delivery date has
 *              passed with no live bill — the supplier invoices still to come.
 *
 * From GET /api/bills/delivered-not-billed (billService.deliveredNotBilled).
 * Each row opens its PO, or starts a bill from it; ticking several of one
 * supplier's (a week of a haulier's PO-Hs) starts one bill for them all, the
 * haulier's figure then split across them on Add Bill.
 * 1.0.1 (2026-10-07): POs of one supplier group tick together too (the bill
 * defaults to the group's parent).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Dropdown from './Dropdown';
import TickCheckbox from './TickCheckbox';
import LoadingSpinner from './LoadingSpinner';
import EntityLink from './EntityLink';
import usePersistedState from '../hooks/usePersistedState';
import { useSlideOutButton } from './SlideOutButton';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import './DeliveredNotBilledPanel.css';

function DeliveredNotBilledPanel({ onNavigate }) {
  const [kind, setKind] = usePersistedState('searchBills.deliveredKind', 'all');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams();
    if (kind !== 'all') qs.set('kind', kind);
    fetch(`${API_URL}/api/bills/delivered-not-billed?${qs}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (!d.success) throw new Error(d.error || 'Could not load');
        setRows(d.rows || []);
        setSummary(d.summary || null);
        setSelected(new Set());
      })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind]);

  const chosen = useMemo(() => rows.filter(r => selected.has(r.id)), [rows, selected]);
  // One supplier, or one supplier group (MVFS depots billed by the parent):
  // the server's supplier_group_id is the group's parent, else the account.
  const supplierKey = (r) => r.supplier_group_id || r.account_id || r.supplier_name;
  const oneSupplier = chosen.length > 0 && chosen.every(r => supplierKey(r) === supplierKey(chosen[0]));
  useSlideOutButton([chosen.length, loading]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const startBill = (ids) => {
    sessionStorage.setItem('createBillFromPOIds', JSON.stringify({ ids, mode: 'combined' }));
    onNavigate && onNavigate('create-bill');
  };

  return (
    <div className="delivered-not-billed">
      <div className="delivered-not-billed-head">
        <div>
          <h3>Delivered, not billed</h3>
          <p className="delivered-not-billed-hint">
            Purchase orders delivered with no bill yet — the supplier invoices still to come.
            {summary ? ` ${summary.count}${summary.limited ? '+' : ''} PO${summary.count === 1 ? '' : 's'}, ${formatCurrency(summary.total)}.` : ''}
          </p>
        </div>
        <div className="delivered-not-billed-actions">
          <div className="filter-group">
            <label>Kind</label>
            <Dropdown
              value={kind}
              onChange={(v) => setKind(v || 'all')}
              options={[
                { value: 'all', label: 'All kinds' },
                { value: 'PO-D', label: 'PO-D (supply)' },
                { value: 'PO-H', label: 'PO-H (haulage)' },
                { value: 'PO-', label: 'PO- (made in Xero)' },
              ]}
            />
          </div>
          <div className="buttons slideout">
            <div>
              <button
                type="button"
                className="btn-slideout"
                disabled={!oneSupplier}
                title={!chosen.length ? 'Tick the POs this bill covers' : oneSupplier ? 'Start one bill for the ticked POs' : 'The ticked POs are from different suppliers'}
                onClick={() => startBill(chosen.map(r => r.id))}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">receipt_long</span></div>
                <span className="btn-label">{chosen.length > 1 ? `Bill ${chosen.length} POs` : 'Bill ticked'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <p className="delivered-not-billed-error">{error}</p>}
      {loading ? <LoadingSpinner /> : (
        <div className="table-container delivered-not-billed-table">
          <table className="data-table">
            <thead>
              <tr>
                <th className="checkbox-col" />
                <th>PO</th>
                <th>Kind</th>
                <th>Supplier</th>
                <th>Customer</th>
                <th>Delivered</th>
                <th className="text-right">Days</th>
                <th className="currency">Total</th>
                <th>Status</th>
                <th className="actions-col" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="10" className="no-results">Nothing delivered is waiting for a bill.</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="checkbox-col">
                    <TickCheckbox id={`dnb-${r.id}`} checked={selected.has(r.id)} onChange={() => toggle(r.id)} ariaLabel={`Tick ${r.purchase_order_number}`} />
                  </td>
                  <td>
                    {/* PurchaseOrderModal opens by the Xero id (CLAUDE.md). */}
                    <EntityLink type="purchase_order" id={r.xero_purchase_order_id}>
                      <strong>{r.purchase_order_number}</strong>
                    </EntityLink>
                  </td>
                  <td>{r.kind}</td>
                  <td>{r.supplier_name || '—'}</td>
                  <td>{r.customer_name || r.customer_order_number || '—'}</td>
                  <td>{formatDateShort(r.delivery_date || r.date)}</td>
                  <td className="text-right">{r.days_since_delivery ?? '—'}</td>
                  <td className="currency">{formatCurrency(r.total)}</td>
                  <td><span className={`status-badge status-${String(r.status || '').toLowerCase()}`}>{r.status}</span></td>
                  <td className="actions-col">
                    <div className="actions-cell">
                      <button type="button" className="btn-icon" title={`Start a bill for ${r.purchase_order_number}`} onClick={() => startBill([r.id])}>
                        <span className="material-symbols-rounded">receipt_long</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DeliveredNotBilledPanel;
